import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgxFileDropEntry } from 'ngx-file-drop';
import { ImportFileComponent } from '@app-components/common/import-file/import-file.component';
import { LoaderComponent } from '@app-components/common/loader/loader.component';
import { NoDataComponent } from '@app-components/common/no-data/no-data.component';
import { ErrorsPipe } from '@app-pipes/error-code.pipe';
import { ContactClient } from '@app-services/api/clients/contact.client';
import { TempFileClient } from '@app-services/api/clients/temp-file.client';
import { MatchError } from '@app-services/errors/error-matcher';
import {
  BaseGetByFileIdRequest,
  BaseGetByIdRequest,
} from '@app-types/base/base';
import { ImportContactState } from '@app-types/enums/contacts';
import { makeElementId } from '@app-utils/common';
import { FormatStateImportContactPipe } from '@app-pipes/format-state-import-contact.pipe';
import {
  ContactImportErrorContract,
  ImportFileItem,
} from '@app-types/api/contact';

@Component({
  selector: 'app-import-contacts',
  templateUrl: './import-contacts.component.html',
  styleUrls: ['./import-contacts.component.scss'],
  imports: [
    ImportFileComponent,
    TranslateModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatTableModule,
    MatSortModule,
    MatTooltipModule,
    LoaderComponent,
    NoDataComponent,
  ],
})
export class ImportContactsComponent implements OnInit {
  @Input() mailAccountId: number | null;
  @Output() closeDrawer = new EventEmitter<void>();

  @ViewChild(MatSort)
  set tableSort(sort: MatSort | undefined) {
    this.importDataSource.sort = sort ?? null;
  }
  importContactState = ImportContactState;
  public isExportingTemplate = false;
  public isLoading = false;
  public progressValue = 0;
  public progressValueCount = 0;
  public textError = '';
  public filesError = '';

  public originalDataSource: Partial<ImportFileItem>[] = [];
  public importDataSource = new MatTableDataSource<Partial<ImportFileItem>>([]);

  public readonly columnHeaders = {
    fileName: 'fileName',
    alias: 'alias',
    email: 'email',
    state: 'status',
    message: 'message',
  };

  public readonly displayedColumns: string[] = [
    'fileName',
    'alias',
    'email',
    'state',
    'message',
  ];

  public readonly accept = [
    'text/csv',
    'application/vnd.ms-excel',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    'text/vcard',
    'text/x-vcard',
    '.csv',
    '.xls',
    '.xlsx',
    '.vcf',
  ];

  public readonly maxFiles = 100;

  public stateOptions: { id: number; title: string }[] = [];
  public filter: number | null = null;

  private formatStateImportContactPipe = new FormatStateImportContactPipe();
  private pipeErrors = new ErrorsPipe();

  constructor(
    private contactClient: ContactClient,
    private tempFileClient: TempFileClient,
    public matchError: MatchError,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.stateOptions = Object.keys(ImportContactState)
      .filter(key => !(parseInt(key, 10) >= 0))
      .map(key => ({
        id: ImportContactState[key as keyof typeof ImportContactState],
        title: this.formatStateImportContactPipe.transform(
          ImportContactState[key as keyof typeof ImportContactState]
        ),
      }));
  }

  getStateLabel(state: number): string {
    return (
      this.formatStateImportContactPipe.transform(
        state as ImportContactState
      ) || ' '
    );
  }

  async exportTemplate(): Promise<void> {
    if (!this.mailAccountId) return;
    this.isExportingTemplate = true;
    try {
      const fileResponse = await this.contactClient.exportTemplate(
        new BaseGetByIdRequest(this.mailAccountId)
      );
      const downloadRequest = new BaseGetByFileIdRequest(
        fileResponse.result.fileId
      );
      await this.tempFileClient.download(downloadRequest);
    } catch (e) {
      this.matchError.errorHandler(e);
      this.matchError.logError(e);
    } finally {
      this.isExportingTemplate = false;
    }
  }

  checkFormat(file: File): boolean {
    return (
      this.accept.includes(file.type) ||
      this.accept.some(type => file.name.endsWith(type.replace('.', '')))
    );
  }

  async importFile(file: File): Promise<void> {
    if (!this.mailAccountId) return;
    try {
      const response = await this.contactClient.importContacts({
        mailAccountId: this.mailAccountId,
        file,
      });
      const files = response.data.map(el => ({
        ...el,
        id: makeElementId(),
        fileName: file.name,
        state: el?.error
          ? ImportContactState.Error
          : el.isDuplicate
            ? ImportContactState.Missed
            : ImportContactState.Imported,
      }));
      this.originalDataSource = [...files, ...this.originalDataSource];
      this.importDataSource.data = [...this.originalDataSource];
    } catch (error: any) {
      const importError: Partial<ImportFileItem> = {
        id: makeElementId(),
        fileName: file.name,
        state: ImportContactState.Error,
        error: {
          code: error?.Code ?? 1,
          message:
            this.pipeErrors.transform(error.Code) ||
            `${error.Code ? `Error code: ${error.Code}` : ''} ${error?.Description || 'something went wrong'}`,
        },
      };
      this.originalDataSource = [importError, ...this.originalDataSource];
      this.importDataSource.data = [...this.originalDataSource];
    } finally {
      this.progressValue += this.progressValueCount;
    }
  }

  async onDrop(event: NgxFileDropEntry[]): Promise<void> {
    this.textError && (this.textError = '');
    this.filesError && (this.filesError = '');
    if (this.maxFiles < event.length) {
      this.textError = 'errorImportEmlMaxCount';
      return;
    }

    const filesQue: File[] = [];
    this.isLoading = true;
    this.progressValue = 1;

    const filePromises = event.map(fileInfo => {
      return new Promise<void>((resolve, reject) => {
        const fileEntry = fileInfo.fileEntry as FileSystemFileEntry;
        fileEntry.file(async (file: File) => {
          if (this.checkFormat(file)) {
            filesQue.push(file);
          } else {
            this.filesError = `${this.filesError} ${file.name}`;
          }
          resolve();
        }, reject);
      });
    });

    await Promise.all(filePromises);
    this.progressValueCount = filesQue.length
      ? Math.round(100 / filesQue.length)
      : 0;
    for (let i = 0; i < filesQue.length; i++) {
      await this.importFile(filesQue[i]);
    }
    this.progressValue = 100;
    this.isLoading = false;
    this.progressValue = 0;
    this.progressValueCount = 0;
    this.filter = null;
  }

  setFilterChange(val: number, remove?: boolean): void {
    this.filter = remove ? null : val;
    this.loadData();
  }

  loadData(searchValue = ''): void {
    if (!searchValue && !this.filter) {
      this.importDataSource.data = this.originalDataSource;
      return;
    }

    this.importDataSource.data = this.originalDataSource.filter(
      el =>
        (searchValue
          ? Object.entries(el).some(([key, value]) => {
              let formattedValue = this.formatSearchValue(key, value);
              this.translate
                .get(formattedValue || ' ')
                .subscribe((res: string) => {
                  formattedValue = res;
                });

              return formattedValue
                .toLowerCase()
                .includes(searchValue.trim().toLowerCase());
            })
          : true) && (this.filter != null ? el.state === this.filter : true)
    );
  }

  reload(): void {
    this.filter = null;
    this.loadData();
  }

  formatSearchValue(key: string, value: any): string {
    if (key === 'state') {
      return this.getStateLabel(Number(value));
    }

    if (key === 'error') {
      const v = value as ContactImportErrorContract;
      return (v?.message ?? '') + (v?.field ? ` ${v.field}` : '');
    }
    if (key === 'isDuplicate') {
      return value ? 'errorDuplicateContact' : '';
    }
    return String(value ?? '');
  }
}
